import {PayloadAction, createSlice, current} from '@reduxjs/toolkit';
import isEqual from 'react-fast-compare';
import {
  Location,
  ReverseGeocode,
  SavedPlace,
  UserLocation,
} from '../../api/types';
import {ForecastModelConfig} from '../../utils/Forecast/types';
import {locationFromSavedPlace} from '../../utils/MyWeather';

type LocationState = {
  locationList: Location[];
  userLocation: UserLocation | null;
  selectedLocation: string | null;

  favoriteLocationHashs: string[];

  forecastModels: Record<string, ForecastModelConfig[]>;
  selectedForecastModel: Record<string, string>;

  /**
   * Account ids (backend#266) of places removed here that the account still
   * holds: the sync deletes them there. Until it has, a /me still lists
   * them and must not bring them back.
   */
  deletedServerIds: string[];
};

export const initialState: LocationState = {
  locationList: [],
  userLocation: null,
  selectedLocation: null,
  favoriteLocationHashs: [],
  forecastModels: {},
  selectedForecastModel: {},
  deletedServerIds: [],
};

export const weatherSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    addLocation: (
      state,
      {payload: newLocation}: {payload: Location | null},
    ) => {
      // A place picked again keeps its account id (backend#266): without it
      // the next /me could not tell it was deleted elsewhere, and the sync
      // would upload it again.
      const previous = state.locationList.find(
        (l) => l.hash === newLocation?.hash,
      );
      const account = previous?.serverId
        ? {
            serverId: previous.serverId,
            ...(previous.serverIdUnconfirmed
              ? {serverIdUnconfirmed: true}
              : {}),
          }
        : null;
      state.locationList = state.locationList
        .filter((l) => l.hash !== newLocation?.hash)
        .concat([
          newLocation && account ? {...newLocation, ...account} : newLocation,
        ]);
    },
    addFavoriteLocation: (
      state,
      {payload: locationHash}: {payload: string},
    ) => {
      state.favoriteLocationHashs.push(locationHash);
    },
    removeFavoriteLocation: (
      state,
      {payload: locationHash}: {payload: string},
    ) => {
      const serverId = state.locationList.find(
        (l) => l.hash === locationHash,
      )?.serverId;
      if (serverId && !state.deletedServerIds.includes(serverId)) {
        state.deletedServerIds.push(serverId);
      }
      state.favoriteLocationHashs = state.favoriteLocationHashs.filter(
        (hash) => hash !== locationHash,
      );
      state.locationList = state.locationList.filter(
        (l) => l.hash !== locationHash,
      );
    },
    /**
     * Undo of a removal from My weather (FE #381): the place back in the list
     * and among the favourites, at `index`. Without its account id, and the
     * delete the removal queued left alone: the sync may have run it
     * already, and it deletes before it uploads, so the place is saved again
     * under a new id either way.
     */
    restoreFavoriteLocation: (
      state,
      {
        payload: {location, index},
      }: PayloadAction<{location: Location; index: number}>,
    ) => {
      if (!state.locationList.some((l) => l.hash === location.hash)) {
        const {
          serverId: _id,
          serverIdUnconfirmed: _unconfirmed,
          ...place
        } = location;
        state.locationList.push(place);
      }
      if (!state.favoriteLocationHashs.includes(location.hash)) {
        state.favoriteLocationHashs.splice(index, 0, location.hash);
      }
    },
    reorderFavoriteLocations: (
      state,
      {payload: orderedHashes}: {payload: string[]},
    ) => {
      state.favoriteLocationHashs = orderedHashes;
    },
    /**
     * Apply the account's saved places (backend#266), as /me returns them.
     *
     * A device favourite and an account place with the same hash are one
     * place: it keeps its position here and takes the account's id and
     * label. Account places new to this device are added as favourites, in
     * the account's order. A favourite that had reached the account and is
     * no longer on it was deleted on another device, and goes. One that
     * never reached it (no `serverId`) is the sync's to upload, and stays.
     */
    hydrateSavedPlaces: (
      state,
      {payload: places}: PayloadAction<SavedPlace[]>,
    ) => {
      // Removed here, not yet deleted there: not to be brought back.
      const incoming = places
        .filter((place) => !state.deletedServerIds.includes(place.id))
        .map(locationFromSavedPlace);
      const live = new Set(incoming.map((l) => l.serverId));
      const incomingHashes = new Set(incoming.map((l) => l.hash));
      // A hash still on the account was re-saved under a new id, not deleted.
      const deleted = new Set(
        state.locationList
          .filter(
            (l) =>
              l.serverId &&
              !l.serverIdUnconfirmed &&
              !live.has(l.serverId) &&
              !incomingHashes.has(l.hash),
          )
          .map((l) => l.hash),
      );
      state.locationList = state.locationList.filter(
        (l) => !deleted.has(l.hash),
      );
      state.favoriteLocationHashs = state.favoriteLocationHashs.filter(
        (hash) => !deleted.has(hash),
      );
      for (const place of incoming) {
        const index = state.locationList.findIndex(
          (l) => l.hash === place.hash,
        );
        if (index === -1) {
          state.locationList.push(place);
        } else {
          // The account's label and subtitle, a cleared subtitle included.
          const {serverIdUnconfirmed: _listed, ...kept} = current(
            state.locationList[index],
          );
          const {subtitle: _deviceSubtitle, ...rest} = kept;
          // No subtitle key at all when the account has none, as
          // locationFromSavedPlace writes it.
          const merged = {...rest, ...place};
          // An unchanged place keeps its object: screens compare them.
          if (!isEqual(current(state.locationList[index]), merged)) {
            state.locationList[index] = merged;
          }
        }
        if (!state.favoriteLocationHashs.includes(place.hash)) {
          state.favoriteLocationHashs.push(place.hash);
        }
      }
    },
    /**
     * Record the account ids the sync answered with (backend#266), by the
     * hash each place was sent under. Unconfirmed until a /me lists them.
     */
    attachSavedPlaceIds: (
      state,
      {payload}: PayloadAction<{hash: string; serverId: string}[]>,
    ) => {
      const byHash = new Map(payload.map((p) => [p.hash, p.serverId]));
      // Saved again before its delete went out: the user wants it now.
      const attached = new Set(byHash.values());
      state.deletedServerIds = state.deletedServerIds.filter(
        (id) => !attached.has(id),
      );
      state.locationList.forEach((location) => {
        const serverId = byHash.get(location.hash);
        if (serverId) {
          location.serverId = serverId;
          location.serverIdUnconfirmed = true;
        }
      });
    },
    /** Account ids to delete there, for the sync (see `deletedServerIds`). */
    queueDeletedPlaces: (state, {payload}: PayloadAction<string[]>) => {
      payload.forEach((id) => {
        if (!state.deletedServerIds.includes(id)) {
          state.deletedServerIds.push(id);
        }
      });
    },
    /** The ids the account no longer holds (deleted, or already gone). */
    forgetDeletedPlaces: (state, {payload}: PayloadAction<string[]>) => {
      state.deletedServerIds = state.deletedServerIds.filter(
        (id) => !payload.includes(id),
      );
    },
    cleanLocationList: (state) => {
      // Keep only favorite locations
      state.locationList = state.locationList.filter((l) =>
        state.favoriteLocationHashs.includes(l.hash),
      );
    },
    /**
     * The GPS entry, or none. A fix at the point already named keeps the
     * name (`place`, `placeLocale`, `title`, `subtitle`): the webapp locates
     * on every visit once allowed and mobile at every launch, and the point
     * is rounded (`MyWeather.userLocationFromFix`), so it is usually the
     * same one. Anywhere else, the fix's own empty name stands, which is
     * what makes `useUserLocationPlace` look the new point up.
     */
    setUserLocation: (
      state,
      {payload: userLocation}: {payload: UserLocation | null},
    ) => {
      const previous = state.userLocation;
      const samePoint =
        previous !== null &&
        userLocation !== null &&
        userLocation.place === undefined &&
        previous.coordinates.latitude === userLocation.coordinates.latitude &&
        previous.coordinates.longitude === userLocation.coordinates.longitude;
      state.userLocation = samePoint
        ? {
            ...userLocation,
            title: previous.title,
            subtitle: previous.subtitle,
            place: previous.place,
            placeLocale: previous.placeLocale,
          }
        : userLocation;
    },

    /**
     * Merge a resolved place name into the GPS location.
     *
     * A merge rather than a `setUserLocation` replace, and guarded on the
     * coordinates, because reverse geocoding resolves asynchronously and the
     * location can change underneath it. Replacing wholesale with a snapshot
     * taken before the request would undo whatever happened in between —
     * including `setUserLocation(null)` when the user revokes the permission,
     * which would put them back on a location they just denied.
     */
    setUserLocationPlace: (
      state,
      {
        payload,
      }: {
        payload: {
          coordinates: {latitude: number; longitude: number};
          place: ReverseGeocode;
          placeLocale: string;
          title: string;
          subtitle?: string;
        };
      },
    ) => {
      const current = state.userLocation;
      if (
        !current ||
        current.coordinates.latitude !== payload.coordinates.latitude ||
        current.coordinates.longitude !== payload.coordinates.longitude
      ) {
        return;
      }
      current.place = payload.place;
      current.placeLocale = payload.placeLocale;
      current.title = payload.title;
      current.subtitle = payload.subtitle;
    },

    setSelectedLocation: (state, {payload: hash}: {payload: string}) => {
      state.selectedLocation = hash;
    },

    // Weather Forecast reducers
    setLocationForecastModels: (
      state,
      {
        payload: {hash, forecastModels},
      }: {
        payload: {
          hash: string;
          forecastModels: ForecastModelConfig[];
        };
      },
    ) => {
      const newForecastModels = {} as Record<number, ForecastModelConfig[]>;
      newForecastModels[hash] = forecastModels;
      state.forecastModels = {...state.forecastModels, ...newForecastModels};
    },
    setLocationSelectedForecastModel: (
      state,
      {
        payload: {locationHash, forecastModel},
      }: {payload: {locationHash: string; forecastModel: string}},
    ) => {
      const newSelectedForecastModel: Record<string, string> = {};
      newSelectedForecastModel[locationHash] = forecastModel;
      state.selectedForecastModel = {
        ...state.selectedForecastModel,
        ...newSelectedForecastModel,
      };
    },
    /**
     * Stop overriding this location. See the weather slice's twin for why this
     * deletes the key instead of blanking it.
     */
    clearLocationSelectedForecastModel: (
      state,
      {payload: {locationHash}}: {payload: {locationHash: string}},
    ) => {
      delete state.selectedForecastModel[locationHash];
    },
  },
});

export const {
  setUserLocation,
  setUserLocationPlace,
  setSelectedLocation,
  setLocationForecastModels,
  setLocationSelectedForecastModel,
  clearLocationSelectedForecastModel,
  addLocation,
  addFavoriteLocation,
  removeFavoriteLocation,
  reorderFavoriteLocations,
  restoreFavoriteLocation,
  hydrateSavedPlaces,
  attachSavedPlaceIds,
  forgetDeletedPlaces,
  queueDeletedPlaces,
  cleanLocationList,
} = weatherSlice.actions;

export default weatherSlice.reducer;
